import { msg } from "@lingui/core/macro";
import { type JSX, ErrorBoundary as SolidErrorBoundary } from "solid-js";
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-solid";
import { cn } from "@comandero/shared";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@/components/ui/glass-surface";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Ilustración SVG inline theme-aware para el fallback de error. Usa
 * `currentColor` + opacidades para heredar el tono del contenedor (sin colores
 * hardcodeados). Decorativa → `aria-hidden`.
 */
function ErrorIllustration(props: { class?: string }) {
  return (
    <svg viewBox="0 0 120 120" class={props.class} aria-hidden="true" role="presentation">
      {/* plato volcado / cloche con incidencia */}
      <circle cx="60" cy="62" r="34" fill="currentColor" opacity="0.07" />
      <circle cx="60" cy="62" r="34" fill="none" stroke="currentColor" stroke-width="2.5" />
      <path
        d="M40 62a20 20 0 0 1 40 0"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
      />
      <path d="M34 62h52" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      <circle cx="60" cy="40" r="3" fill="currentColor" />
      <path
        d="M60 24v6"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        opacity="0.7"
      />
      <path
        d="M82 30l-4 4M38 30l4 4"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        opacity="0.5"
      />
    </svg>
  );
}

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  /** Título corto opcional (por defecto: genérico premium). */
  title?: string;
  /** Si true, ocupa el alto disponible y centra (regiones de página). */
  fill?: boolean;
  class?: string;
}

/**
 * Fallback premium reutilizable: ilustración + título + descripción + acciones
 * "Reintentar" (reset del boundary) y "Recargar" (reload duro). Theme-safe.
 */
function ErrorFallback(props: ErrorFallbackProps) {
  const { t } = useI18n();

  const detail = (): string => {
    if (props.error instanceof Error) {
      return props.error.message;
    }
    if (typeof props.error === "string") {
      return props.error;
    }
    return t(msg`An unexpected error has occurred.`);
  };

  return (
    <div
      role="alert"
      data-testid="error-boundary-fallback"
      class={cn(
        "flex w-full items-center justify-center p-4",
        props.fill ? "min-h-[60vh] flex-1" : "py-12",
        props.class,
      )}
    >
      <GlassSurface class="flex w-full max-w-md flex-col items-center gap-6 p-8 text-center">
        <div class="text-accent/70">
          <ErrorIllustration class="h-28 w-28" />
        </div>
        <div class="space-y-2">
          <h2 class="flex items-center justify-center gap-2 text-2xl font-bold tracking-tight">
            <AlertTriangle class="h-6 w-6 text-destructive" aria-hidden="true" />
            {props.title ?? t(msg`Something went wrong`)}
          </h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`This section could not be displayed. You can try again without losing your data.`,
            )}
          </p>
          <p class="break-words text-xs text-muted-foreground/70">{detail()}</p>
        </div>
        <div class="flex w-full gap-3">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RefreshCw class="h-4 w-4" aria-hidden="true" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RotateCcw class="h-4 w-4" aria-hidden="true" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** Título corto opcional para el fallback. */
  title?: string;
  /** Etiqueta de la región (para el log). */
  region?: string;
  /** Ocupa alto disponible y centra (regiones de página). */
  fill?: boolean;
  class?: string;
}

/**
 * `<ErrorBoundary>` de Solid con fallback premium reutilizable. Captura errores
 * de render en su subárbol para que un fallo localizado nunca deje pantalla en
 * blanco. Loggea el error con la región para diagnóstico.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  return (
    <SolidErrorBoundary
      fallback={(error: unknown, reset: () => void) => {
        console.error(`[ErrorBoundary${props.region ? `:${props.region}` : ""}]`, error);
        return (
          <ErrorFallback
            error={error}
            reset={reset}
            title={props.title}
            fill={props.fill}
            class={props.class}
          />
        );
      }}
    >
      {props.children}
    </SolidErrorBoundary>
  );
}
